import { Box, Chip, Typography } from "@mui/material";
import { COLORS } from "assets";
import { useContextController } from "context";
import { useCallback } from "react";
import { IncidentActor } from "incidents/types";

export function usePhrase() {
  const [{ locale }] = useContextController();
  return useCallback((en: string, fr: string) => (locale === "fr" ? fr : en), [locale]);
}

export function useActor(previewRole?: "nurse" | "supervisor"): IncidentActor {
  const [{ userData }] = useContextController();
  if (previewRole === "supervisor") {
    return { user_id: "preview-supervisor", name: "Preview manager", designation: "Director of care" };
  }
  if (previewRole === "nurse") {
    return { user_id: "preview-nurse", name: "Preview nurse", designation: "RPN" };
  }
  return {
    user_id: userData?._id || "unknown",
    name: userData?.name || "Staff member",
    designation: userData?.role === "admin" || userData?.type === "admin" ? "Manager" : undefined
  };
}

export function SourceTag({ filledBySystem }: { filledBySystem: boolean }) {
  const phrase = usePhrase();
  const label = filledBySystem
    ? phrase("Filled by NeuraVue", "Rempli par NeuraVue")
    : phrase("Completed by staff", "Complété par le personnel");
  return (
    <Chip
      size="small"
      label={label}
      sx={{
        height: 26,
        fontWeight: 600,
        fontSize: 12,
        bgcolor: filledBySystem ? "#EEF1F4" : "rgba(30, 152, 217, 0.12)",
        color: COLORS.surface.primaryText,
        border: `1px solid ${COLORS.surface.border}`
      }}
    />
  );
}

export function SectionTitle({ title, hint }: { title: string; hint?: string }) {
  return (
    <Box mb={1.5}>
      <Typography component="h2" sx={{ fontSize: 18, fontWeight: 700, color: COLORS.surface.primaryText }}>
        {title}
      </Typography>
      {hint ? (
        <Typography sx={{ color: COLORS.surface.secondaryText, fontSize: 14, mt: 0.5 }}>{hint}</Typography>
      ) : null}
    </Box>
  );
}

export const fieldSx = {
  "& .MuiInputBase-root": { bgcolor: COLORS.surface.card, minHeight: 44 },
  "& .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.surface.border },
  "& .MuiInputLabel-root": { color: COLORS.surface.secondaryText },
  "& .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: COLORS.primary }
};

export const primaryButtonSx = {
  minHeight: 44,
  textTransform: "none" as const,
  fontWeight: 600,
  borderRadius: `${COLORS.radius.md}px`
};
